/**
 * 行銷活動管理 API 服務
 * 使用 RTK Query 封裝行銷活動相關的 API 調用
 * 
 * 注意：目前使用 mock 數據進行開發測試
 * 待 API 整合後，將 mockBaseQuery 替換為 baseQuery
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from '@/services/baseApi';

import { IApiResponse, IPaginatedResponse } from '@/types/api';
import {
  IMarketingCampaign,
  IMarketingListParams,
  IMarketingFormData,
  IBatchUpdateMarketingRequest,
  IBatchUpdateMarketingResponse,
  IMarketingStatistics,
} from '@/types/marketing';
import { ConstantUtil } from '@/utils/ConstantUtil';

/**
 * 行銷活動 API 服務
 */
export const marketingApi = createApi({
  reducerPath: 'marketingApi',
  baseQuery: baseQueryWithErrorHandling,
  // baseQuery, // 真實 API baseQuery（待 API 整合後使用）
  tagTypes: ['Marketing', 'MarketingStatistics'],
  endpoints: (builder) => ({
    // 獲取活動列表
    getMarketingCampaigns: builder.query<
      IApiResponse<IPaginatedResponse<IMarketingCampaign>>,
      IMarketingListParams
    >({
      query: (params) => ({
        url: ConstantUtil.API_MARKETING,
        method: 'GET',
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? ConstantUtil.PAGE_SIZE,
          ...(params.search && { search: params.search }),
          ...(params.searchKeyword && { searchKeyword: params.searchKeyword }),
          ...(params.status && params.status !== 'all' && { status: params.status }),
          ...(params.type && params.type !== 'all' && { type: params.type }),
          ...(params.dateFilter && params.dateFilter !== 'all' && { dateFilter: params.dateFilter }),
          ...(params.startDate && { startDate: params.startDate }),
          ...(params.endDate && { endDate: params.endDate }),
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortOrder && { sortOrder: params.sortOrder }),
        },
      }),
      providesTags: ['Marketing'],
    }),

    // 獲取活動詳情
    getMarketingCampaignById: builder.query<IApiResponse<IMarketingCampaign>, string>({
      query: (id) => ({
        url: ConstantUtil.API_MARKETING_BY_ID(id),
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'Marketing', id }],
    }),

    // 創建活動
    createMarketingCampaign: builder.mutation<IApiResponse<IMarketingCampaign>, IMarketingFormData>({
      query: (data) => ({
        url: ConstantUtil.API_MARKETING_CREATE,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Marketing', 'MarketingStatistics'],
    }),

    // 更新活動
    updateMarketingCampaign: builder.mutation<
      IApiResponse<IMarketingCampaign>,
      { id: string; data: Partial<IMarketingFormData> }
    >({
      query: ({ id, data }) => ({
        url: ConstantUtil.API_MARKETING_UPDATE(id),
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Marketing', id },
        'Marketing',
        'MarketingStatistics',
      ],
    }),

    // 刪除活動（軟刪除）
    deleteMarketingCampaign: builder.mutation<IApiResponse<void>, string>({
      query: (id) => ({
        url: ConstantUtil.API_MARKETING_DELETE(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['Marketing', 'MarketingStatistics'],
    }),

    // 批量更新活動
    batchUpdateMarketingCampaigns: builder.mutation<
      IApiResponse<IBatchUpdateMarketingResponse>,
      IBatchUpdateMarketingRequest
    >({
      query: (data) => ({
        url: ConstantUtil.API_MARKETING_BATCH_UPDATE,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Marketing', 'MarketingStatistics'],
    }),

    // 複製活動
    duplicateMarketingCampaign: builder.mutation<IApiResponse<IMarketingCampaign>, string>({
      query: (id) => ({
        url: ConstantUtil.API_MARKETING_DUPLICATE(id),
        method: 'POST',
      }),
      invalidatesTags: ['Marketing', 'MarketingStatistics'],
    }),

    // 獲取活動統計
    getMarketingStatistics: builder.query<IApiResponse<IMarketingStatistics>, void>({
      query: () => ({
        url: ConstantUtil.API_MARKETING_STATISTICS,
        method: 'GET',
      }),
      providesTags: ['MarketingStatistics'],
    }),
  }),
});

// 導出 hooks
export const {
  useGetMarketingCampaignsQuery,
  useGetMarketingCampaignByIdQuery,
  useCreateMarketingCampaignMutation,
  useUpdateMarketingCampaignMutation,
  useDeleteMarketingCampaignMutation,
  useBatchUpdateMarketingCampaignsMutation,
  useDuplicateMarketingCampaignMutation,
  useGetMarketingStatisticsQuery,
} = marketingApi;

